import { css } from '@emotion/react';
import { v2CssVariables } from './components/v2/palette';
import checkqueredBackgroundPNG from './~reusables/images/checkquered-background.png';

// Dark-mode colours for Ant Design's preset tag colours: a tinted background
// and border with light text, and a deep fill for the solid variant.
const darkTagColors: Record<
  string,
  { rgb: string; solid: string; text: string }
> = {
  blue: { rgb: '59, 130, 246', solid: '#1d4ed8', text: '#93c5fd' },
  cyan: { rgb: '6, 182, 212', solid: '#0e7490', text: '#67e8f9' },
  geekblue: { rgb: '99, 102, 241', solid: '#4338ca', text: '#a5b4fc' },
  gold: { rgb: '234, 179, 8', solid: '#a16207', text: '#fde047' },
  green: { rgb: '34, 197, 94', solid: '#15803d', text: '#86efac' },
  lime: { rgb: '132, 204, 22', solid: '#4d7c0f', text: '#bef264' },
  magenta: { rgb: '236, 72, 153', solid: '#be185d', text: '#f9a8d4' },
  orange: { rgb: '249, 115, 22', solid: '#c2410c', text: '#fdba74' },
  purple: { rgb: '168, 85, 247', solid: '#7e22ce', text: '#d8b4fe' },
  red: { rgb: '239, 68, 68', solid: '#b91c1c', text: '#fca5a5' },
  volcano: { rgb: '234, 88, 12', solid: '#c2410c', text: '#fdba74' },
};

// The selectors match Ant Design's own (".ant-tag.ant-tag-green:not(...)
// .ant-tag-solid") so that, nested under [data-theme='dark'], they win.
const darkTagRules = Object.entries(darkTagColors)
  .map(([name, { rgb, solid, text }]) => {
    const tag = `.ant-tag.ant-tag-${name}:not(.ant-tag-disabled)`;
    return `
    ${tag}.ant-tag-filled,
    ${tag}.ant-tag-outlined {
      background: rgba(${rgb}, 0.14);
      color: ${text};
    }
    ${tag}.ant-tag-outlined {
      border-color: rgba(${rgb}, 0.4);
    }
    ${tag}.ant-tag-solid {
      background: ${solid};
      border-color: ${solid};
      color: #ffffff;
    }`;
  })
  .join('\n');

// This is the default CSS included in create-react-app.
export const globalCSS = css`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  /* Light theme (default) */
  :root,
  [data-theme='light'] {
    color-scheme: light;
    --layout-page-bg: #fafafa;
    --layout-card-bg: #ffffff;
    --layout-card-header-bg: #ffffff;
    --layout-card-split-bg: #f5f5f5;
    --layout-card-title: #333;
    --layout-card-text: #666;
    --layout-card-text-secondary: #999;
    --layout-accent: #3946f1;
    --layout-accent-bg: #f1f2ff;
    --layout-card-border: #e9e9ec;
    --layout-nav-item-bg: transparent;
    --layout-nav-item-bg-hover: #f5f5f5;
    --layout-nav-item-bg-active: #f5f5f5;
    --layout-nav-item-text: #666;
    --layout-nav-item-text-hover: #333;
    --layout-nav-item-text-active: #333;
    --layout-nav-item-icon: #444;
    --layout-nav-item-icon-active: #3946f1;
    --layout-nav-section-title: #999;
    --layout-breadcrumb-icon-bg: #f5f5f5;
    --layout-breadcrumb-text: #333;
    --layout-breadcrumb-separator: #ccc;
    --layout-user-name: #333;
    --layout-user-email: #666;
    --layout-status-color: #22c55e;
    --layout-external-chevron: #999;
    --layout-checkbox-ring: #8b8b97;
    --layout-checkbox-checked-bg: #3946f1;
    --layout-checkbox-checked-bg-hover: #2f3bd9;
    --layout-checkbox-tick: #ffffff;
    --nav-bg: #ffffff;
    --nav-border: #e9e9ec;
    --nav-text: #1a1a1a;
    --nav-text-muted: #5c5c66;
    --nav-text-subtle: #6f6f7a;
    --nav-separator: #d3d3d8;
    --nav-hover-bg: #f2f2f5;
    --nav-selected-bg: #f1f2ff;
    --nav-accent: #3946f1;
    --nav-input-bg: #fafafb;
    --nav-input-border: #e4e4e8;
    --nav-menu-shadow: 0 16px 40px rgba(20, 20, 40, 0.12);
    --nav-subtabs-bg: #ececf0;
    --nav-subtab-active-bg: #ffffff;
    --nav-running-bg: #fff7ec;
    --nav-running-border: #fde3bf;
    --nav-running-text: #6b3500;
    --nav-running-accent: #d97706;
    --nav-success-bg: #effbf3;
    --nav-success-border: #bfe8cd;
    --nav-success-text: #0f5132;
    --nav-success-accent: #16a34a;
    --nav-failed-bg: #fef2f2;
    --nav-failed-border: #f6cccc;
    --nav-failed-text: #8a1c1c;
    --nav-failed-accent: #dc2626;
    --nav-segment-empty: #f3e3cc;
    --logo-fill: #3946f1;
    --logo-inlay: #c3c8ff;
    --layout-danger-text: #c62828;
    --layout-danger-border: #f3b4b0;
    --layout-danger-bg-hover: #fdf1f0;
    /* Status surfaces: a tinted background, border and readable text */
    --layout-success-bg: #f6ffed;
    --layout-success-border: #b7eb8f;
    /* The fainter border of a "previous release" tag */
    --layout-success-border-faint: #c4fd98;
    --layout-success-text: #237804;
    --layout-warning-bg: #fffbe6;
    --layout-warning-border: #ffe58f;
    --layout-warning-text: #874d00;
    --layout-error-bg: #fff2f0;
    --layout-error-border: #ffccc7;
    --layout-error-text: #a8071a;
    --layout-info-bg: #f1f2ff;
    --layout-info-border: #c3c8ff;
    --layout-info-text: #2733c9;
    /* The old greys scale in ThemeContext */
    --grey-0: #272727;
    --grey-1: #484849;
    --grey-2: #686868;
    --grey-3: #7c7c7d;
    --grey-4: #a6a6a7;
    --grey-5: #c4c4c5;
    --grey-6: #e6e6e7;
    --grey-7: #fbfbfc;
    --grey-8: #fafafa;
    --grey-9: #fbfbfc;
    /* Chart series, in order, then gridlines and the zero line */
    --layout-chart-1: #2a78d6;
    --layout-chart-2: #eb6834;
    --layout-chart-3: #1baf7a;
    --layout-chart-grid: rgba(0, 0, 0, 0.08);
    --layout-chart-baseline: rgba(0, 0, 0, 0.35);
    /* The v2 pages' colors (components/v2/palette.ts) */
    ${v2CssVariables('light')}
  }

  /* Dark theme */
  [data-theme='dark'] {
    /* Native scrollbars and form controls follow the theme */
    color-scheme: dark;
    --layout-page-bg: #1a1a1a;
    --layout-card-bg: #2a2a2a;
    --layout-card-header-bg: #2a2a2a;
    --layout-card-split-bg: #333;
    --layout-card-title: #e0e0e0;
    --layout-card-text: #b0b0b0;
    --layout-card-text-secondary: #888;
    --layout-accent: #8c95ff;
    --layout-accent-bg: #2a2d4a;
    --layout-card-border: #333333;
    --layout-nav-item-bg: transparent;
    --layout-nav-item-bg-hover: #333;
    --layout-nav-item-bg-active: #333;
    --layout-nav-item-text: #999;
    --layout-nav-item-text-hover: #e0e0e0;
    --layout-nav-item-text-active: #e0e0e0;
    --layout-nav-item-icon: #888;
    --layout-nav-item-icon-active: #568fff;
    --layout-nav-section-title: #666;
    --layout-breadcrumb-icon-bg: #333;
    --layout-danger-text: #ff8a80;
    --layout-danger-border: rgba(255, 99, 88, 0.45);
    --layout-danger-bg-hover: rgba(255, 99, 88, 0.12);
    --layout-breadcrumb-text: #e0e0e0;
    --layout-breadcrumb-separator: #555;
    --layout-user-name: #e0e0e0;
    --layout-user-email: #999;
    --layout-status-color: #22c55e;
    --layout-external-chevron: #666;
    --layout-checkbox-ring: #74747e;
    --layout-checkbox-checked-bg: #5a65f6;
    --layout-checkbox-checked-bg-hover: #6a74f7;
    --layout-checkbox-tick: #ffffff;
    --nav-bg: #202020;
    --nav-border: #333333;
    --nav-text: #e6e6e6;
    --nav-text-muted: #a3a3ab;
    --nav-text-subtle: #8a8a94;
    --nav-separator: #4a4a50;
    --nav-hover-bg: #2c2c2e;
    --nav-selected-bg: #2a2d4a;
    --nav-accent: #8c95ff;
    --nav-input-bg: #262626;
    --nav-input-border: #3a3a3a;
    --nav-menu-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
    --nav-subtabs-bg: #2a2a2a;
    --nav-subtab-active-bg: #3a3a3a;
    --nav-running-bg: #33260f;
    --nav-running-border: #5a4220;
    --nav-running-text: #f5c98a;
    --nav-running-accent: #f59e0b;
    --nav-success-bg: #11281b;
    --nav-success-border: #24513a;
    --nav-success-text: #93e0b2;
    --nav-success-accent: #22c55e;
    --nav-failed-bg: #321517;
    --nav-failed-border: #5c2629;
    --nav-failed-text: #f6a8a8;
    --nav-failed-accent: #ef4444;
    --nav-segment-empty: #4a3a22;
    --logo-fill: #ffffff;
    --logo-inlay: #3b4196;
    --layout-success-bg: rgba(34, 197, 94, 0.12);
    --layout-success-border: rgba(34, 197, 94, 0.35);
    --layout-success-border-faint: #22401a;
    --layout-success-text: #86efac;
    --layout-warning-bg: rgba(234, 179, 8, 0.12);
    --layout-warning-border: rgba(234, 179, 8, 0.35);
    --layout-warning-text: #fde047;
    --layout-error-bg: rgba(239, 68, 68, 0.12);
    --layout-error-border: rgba(239, 68, 68, 0.38);
    --layout-error-text: #fca5a5;
    --layout-info-bg: #2a2d4a;
    --layout-info-border: #3b4196;
    --layout-info-text: #b7bcff;
    --grey-0: #f0f0f0;
    --grey-1: #d6d6d8;
    --grey-2: #b0b0b0;
    --grey-3: #9a9a9f;
    --grey-4: #7a7a80;
    --grey-5: #5a5a60;
    --grey-6: #3a3a3e;
    --grey-7: #303030;
    --grey-8: #262626;
    --grey-9: #303030;
    --layout-chart-1: #3987e5;
    --layout-chart-2: #d95926;
    --layout-chart-3: #199e70;
    --layout-chart-grid: rgba(255, 255, 255, 0.1);
    --layout-chart-baseline: rgba(255, 255, 255, 0.4);
    ${v2CssVariables('dark')}
  }

  body {
    margin: 0;
    padding: 0;
    font-family:
      'Inter',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: var(--layout-page-bg) !important;
    transition: background-color 0.2s ease;
  }
  .ant-dropdown-menu-item-group-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-info:before {
    display: none;
    content: '';
  }

  a:focus {
    text-decoration: none !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-info {
    background: url(${checkqueredBackgroundPNG}) !important;
  }

  .release-eligibility-popover .ant-popover-inner-content {
    padding: 0 !important;
  }

  .ant-form-item-label label {
    font-weight: 500;
    font-size: 15px;
    color: var(--layout-card-title);
  }

  /* Ant Design Card theming */
  /* Cards are flat: a hairline border instead of a drop shadow. */
  .ant-card {
    background: var(--layout-card-bg) !important;
    border: 1px solid var(--layout-card-border) !important;
    box-shadow: none !important;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;
  }

  .ant-card-head {
    background: var(--layout-card-header-bg) !important;
    border-bottom-color: var(--layout-card-border) !important;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;
  }

  .ant-card-head-title {
    color: var(--layout-card-title) !important;
    transition: color 0.2s ease;
  }

  .ant-card-body {
    color: var(--layout-card-text);
    transition: color 0.2s ease;
  }

  /* On phones, header actions wrap under the title instead of pushing the
     card wider than the screen */
  @media (max-width: 768px) {
    .ant-card .ant-card-head {
      padding-block: 12px;
    }

    .ant-card .ant-card-head-wrapper {
      flex-wrap: wrap;
      row-gap: 10px;
    }

    .ant-card .ant-card-head-title {
      flex: 0 0 100%;
      white-space: normal;
    }

    .ant-card .ant-card-extra {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-inline-start: 0;
      max-width: 100%;
    }

    .ant-card .ant-card-extra > * {
      flex-wrap: wrap;
      max-width: 100%;
    }

    .ant-card .ant-card-extra .ant-btn {
      max-width: 100%;
      white-space: normal;
      height: auto;
      min-height: 32px;
    }
  }

  /* Form elements in dark mode */
  .ant-form-item-label > label {
    color: var(--layout-card-title) !important;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector,
  .ant-picker {
    background: var(--layout-card-bg) !important;
    border-color: var(--layout-card-border) !important;
    color: var(--layout-card-text) !important;
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease,
      color 0.2s ease;
  }

  .ant-input::placeholder,
  .ant-select-selection-placeholder {
    color: var(--layout-card-text-secondary) !important;
  }

  .ant-select-selection-item {
    color: var(--layout-card-text) !important;
  }

  /* Table theming */
  .ant-table {
    background: var(--layout-card-bg) !important;
    color: var(--layout-card-text) !important;
  }

  .ant-table-thead > tr > th {
    background: var(--layout-card-header-bg) !important;
    color: var(--layout-card-title) !important;
    border-bottom-color: var(--layout-card-border) !important;
  }

  .ant-table-tbody > tr > td {
    border-bottom-color: var(--layout-card-border) !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: var(--layout-nav-item-bg-hover) !important;
  }

  /* Empty state */
  .ant-empty-description {
    color: var(--layout-card-text-secondary) !important;
  }

  /* Tabs theming */
  .ant-tabs-tab {
    color: var(--layout-card-text) !important;
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--layout-nav-item-icon-active) !important;
  }

  .ant-tabs-ink-bar {
    background: var(--layout-nav-item-icon-active) !important;
  }

  /* Alert/message theming */
  .ant-alert {
    background: var(--layout-card-bg);
    border-color: var(--layout-card-border);
  }

  .ant-alert-message {
    color: var(--layout-card-title);
  }

  .ant-alert-description {
    color: var(--layout-card-text);
  }

  /* Tag theming - default (uncolored) tags */
  .ant-tag {
    background: var(--layout-nav-item-bg-hover);
    /*border-color: var(--layout-card-border);*/
    color: var(--layout-card-text);
  }

  /* Solid green tags ("Latest release"): white text needs a deeper green.
     "html" outranks Ant Design's selector; the dark rules outrank this. */
  html .ant-tag.ant-tag-green:not(.ant-tag-disabled).ant-tag-solid {
    background: #237804;
    border-color: #237804;
  }

  /* Typography theming */
  .ant-typography {
    color: var(--layout-card-text);
  }

  .ant-typography.ant-typography-secondary {
    color: var(--layout-card-text-secondary) !important;
  }

  h1.ant-typography,
  h2.ant-typography,
  h3.ant-typography,
  h4.ant-typography,
  h5.ant-typography,
  .ant-typography h1,
  .ant-typography h2,
  .ant-typography h3,
  .ant-typography h4,
  .ant-typography h5 {
    color: var(--layout-card-title);
  }

  /* Button theming (non-primary) */
  .ant-btn-default {
    background: var(--layout-card-bg);
    border-color: var(--layout-card-border);
    color: var(--layout-card-text);
  }

  .ant-btn-default:hover {
    background: var(--layout-nav-item-bg-hover);
    border-color: var(--layout-card-border);
    color: var(--layout-card-title);
  }

  /* Outlined danger buttons keep their red, which the default-button theming
     above would otherwise turn grey */
  .ant-btn-default.ant-btn-dangerous:not(:disabled) {
    color: var(--layout-danger-text);
    border-color: var(--layout-danger-border);
  }

  .ant-btn-default.ant-btn-dangerous:not(:disabled):hover {
    color: var(--layout-danger-text);
    border-color: var(--layout-danger-text);
    background: var(--layout-danger-bg-hover);
  }

  .ant-btn-dashed {
    background: transparent;
    border-color: var(--layout-card-border);
  }

  /* Modal theming */
  .ant-modal-content {
    background: var(--layout-card-bg) !important;
  }

  .ant-modal-header {
    background: var(--layout-card-bg) !important;
    border-bottom-color: var(--layout-card-border) !important;
  }

  .ant-modal-title {
    color: var(--layout-card-title) !important;
  }

  .ant-modal-close {
    color: var(--layout-card-text) !important;
  }

  .ant-modal-footer {
    border-top-color: var(--layout-card-border) !important;
  }

  /* Popover theming */
  .ant-popover-inner {
    background: var(--layout-card-bg) !important;
  }

  .ant-popover-title {
    color: var(--layout-card-title) !important;
    border-bottom-color: var(--layout-card-border) !important;
  }

  .ant-popover-inner-content {
    color: var(--layout-card-text) !important;
  }

  /* Spin/Loading theming */
  .ant-spin-text {
    color: var(--layout-card-text);
  }

  /* Breadcrumb theming */
  .ant-breadcrumb {
    color: var(--layout-card-text);
  }

  .ant-breadcrumb a {
    color: var(--layout-card-text) !important;
  }

  .ant-breadcrumb-separator {
    color: var(--layout-breadcrumb-separator);
  }

  /* Checkbox/Radio theming */
  .ant-checkbox-wrapper,
  .ant-radio-wrapper {
    color: var(--layout-card-text);
  }

  .ant-radio:not(.ant-radio-checked) .ant-radio-inner {
    background: var(--layout-card-bg);
  }

  /* List theming */
  .ant-list-item {
    border-bottom-color: var(--layout-card-border) !important;
  }

  .ant-list-item-meta-title {
    color: var(--layout-card-title) !important;
  }

  .ant-list-item-meta-description {
    color: var(--layout-card-text-secondary) !important;
  }

  /* Select dropdown popup theming */
  .ant-select-dropdown {
    background: var(--layout-card-bg) !important;
  }

  .ant-select-item {
    color: var(--layout-card-text) !important;
  }

  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background: var(--layout-nav-item-bg-hover) !important;
  }

  .ant-select-item-option-selected {
    color: var(--layout-card-title) !important;
    font-weight: 500;
  }

  /* Select borderless variant text color */
  .ant-select-borderless .ant-select-selection-item {
    color: var(--layout-breadcrumb-text) !important;
  }

  .ant-select-single .ant-select-selector .ant-select-selection-item {
    color: var(--layout-card-text) !important;
  }

  .ant-select-borderless .ant-select-selector .ant-select-selection-item {
    color: var(--layout-breadcrumb-text) !important;
  }

  .ant-select-content-value {
    color: var(--layout-breadcrumb-text) !important;
  }

  /* .hue-horizontal {
    background: linear-gradient(to right, #00f 0%, #f0f 17%, #f00 33%, #f00 50%, #ff0 67%, #0f0 83%, #0ff 100%) !important;
  } */

  /* Hide visually but still readable */
  .visually-hidden {
    position: absolute;
    overflow: hidden;
    clip: rect(0 0 0 0);
    height: 1px;
    width: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
  }

  /* Add spacing between icons and text in tabs */
  .ant-tabs-tab-btn > .anticon {
    margin-right: 12px;
  }

  /* Ant Design 6 controls and coloured tags keep their light palette, so
     dark mode needs its own. */
  [data-theme='dark'] {
    .ant-select-outlined {
      background: var(--layout-card-bg);
      border-color: var(--layout-card-border);
      color: var(--layout-card-text);
    }

    .ant-select-placeholder {
      color: var(--nav-text-subtle);
    }

    .ant-select-arrow,
    .ant-select-clear {
      background: transparent;
      color: var(--nav-text-subtle);
    }

    .ant-checkbox-inner {
      background: var(--layout-card-bg);
      border-color: var(--nav-separator);
    }

    .ant-tag.ant-tag-default.ant-tag-outlined {
      border-color: var(--nav-separator);
    }

    .ant-popover {
      --antd-arrow-background-color: var(--layout-card-bg);
    }

    .ant-popover-container {
      background: var(--layout-card-bg);
      box-shadow:
        0 0 0 1px var(--layout-card-border),
        var(--nav-menu-shadow);
    }

    .ant-popover-content {
      color: var(--layout-card-text);
    }

    .ant-popover-arrow::before {
      background: var(--layout-card-bg);
    }

    .ant-collapse:not(.ant-collapse-ghost) {
      background: var(--layout-card-header-bg);
      border-color: var(--layout-card-border);
    }

    .ant-collapse > .ant-collapse-item {
      border-bottom-color: var(--layout-card-border);
    }

    .ant-collapse > .ant-collapse-item > .ant-collapse-header {
      color: var(--layout-card-title);
    }

    .ant-collapse:not(.ant-collapse-ghost):not(.ant-collapse-borderless)
      > .ant-collapse-item
      > .ant-collapse-panel {
      background: var(--layout-card-bg);
      border-top-color: var(--layout-card-border);
      color: var(--layout-card-text);
    }

    /* Surfaces Ant Design still paints white */
    .ant-alert {
      background: var(--layout-card-split-bg) !important;
      border-color: var(--layout-card-border) !important;
    }

    .ant-alert-title,
    .ant-alert-message {
      color: var(--layout-card-title);
    }

    .ant-alert-description {
      color: var(--layout-card-text);
    }

    .ant-table-wrapper .ant-table-placeholder,
    .ant-table-wrapper .ant-table-placeholder > td,
    .ant-table-wrapper .ant-table-placeholder:hover > td {
      background: var(--layout-card-bg);
    }

    /* The overlay over content that is still loading */
    .ant-spin-container.ant-spin-blur::after,
    .ant-spin-container::after {
      background: var(--layout-card-bg);
    }

    /* The brand blue is too dark to read on dark cards */
    .ant-card a:not(.ant-btn),
    .ant-card .ant-btn-variant-link.ant-btn-color-primary,
    .ant-card .ant-btn-variant-link.ant-btn-color-link {
      color: #9aa1ff;
    }

    .ant-card a:not(.ant-btn):hover,
    .ant-card .ant-btn-variant-link.ant-btn-color-primary:hover,
    .ant-card .ant-btn-variant-link.ant-btn-color-link:hover {
      color: #b7bcff;
    }

    /* Selected menu items (the app switcher's current app) use the brand
       blue as text, which is too dark here */
    .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item-selected {
      background: var(--nav-selected-bg);
      color: var(--nav-accent);
    }

    /* "No data" illustrations draw in fixed light greys */
    .ant-empty-image svg ellipse {
      fill: var(--layout-card-split-bg);
    }

    .ant-empty-image svg g[stroke] {
      stroke: var(--nav-separator);
    }

    .ant-empty-image svg path[fill] {
      fill: var(--layout-card-split-bg);
    }

    ${darkTagRules}
  }
`;
